import React, { useEffect, useRef, useState } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { Panel, Stat } from "@/components/Panel";
import { ResponsiveContainer, LineChart, Line, XAxis, YAxis, Tooltip, CartesianGrid, Area, AreaChart } from "recharts";
import { Brain, Database, DollarSign, Activity, FileDown, BookOpen, Sparkles, Volume2, Loader2, Pause, Camera } from "lucide-react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import PdfViewerModal from "@/components/PdfViewerModal";
import WalkthroughRecorder from "@/components/WalkthroughRecorder";
import CurrencyMismatchBanner from "@/components/CurrencyMismatchBanner";

const SYMS = { USD:"$", EUR:"€", GBP:"£", NGN:"₦", ZAR:"R", KES:"KSh", GHS:"₵", EGP:"£", INR:"₹", BRL:"R$", MXN:"$", AED:"د.إ", SAR:"﷼", JPY:"¥", CNY:"¥", AUD:"A$", CAD:"C$", SGD:"S$" };
const fmt = (n, code) => {
  const s = SYMS[code] || (code ? `${code} ` : "$");
  const abs = Math.abs(n || 0);
  if (abs >= 1e9) return `${s}${(n/1e9).toFixed(2)}B`;
  if (abs >= 1e6) return `${s}${(n/1e6).toFixed(1)}M`;
  if (abs >= 1e3) return `${s}${(n/1e3).toFixed(1)}K`;
  return `${s}${n}`;
};
const fmtNum = (n) => n >= 1_000_000 ? `${(n/1e6).toFixed(2)}M` : n >= 1_000 ? `${(n/1e3).toFixed(1)}K` : `${n}`;

export default function Overview() {
  const { user } = useAuth();
  const [m, setM] = useState(null);
  const [insights, setInsights] = useState([]);
  const [pdfModal, setPdfModal] = useState({ open: false, path: "", filename: "" });
  const [recorderOpen, setRecorderOpen] = useState(false);
  const [canRecord, setCanRecord] = useState(false);
  const [isAdmin, setIsAdmin] = useState(false);
  const [briefing, setBriefing] = useState(null);
  const [briefingLoading, setBriefingLoading] = useState(false);
  const [audioState, setAudioState] = useState("idle"); // idle | loading | playing
  const [wsCcy, setWsCcy] = useState("USD");
  const audioRef = useRef(null);

  useEffect(() => {
    api.get("/dashboard/metrics").then(r => setM(r.data)).catch(() => {});
    api.get("/insights").then(r => setInsights(r.data.slice(0, 4))).catch(() => {});
    api.get("/briefings/latest").then(r => setBriefing(r.data)).catch(() => {});
    api.get("/walkthroughs/_meta/eligibility").then(r => setCanRecord(!!r.data?.allowed)).catch(() => setCanRecord(false));
    api.get("/admin/me").then(r => setIsAdmin(!!r.data?.is_super_admin)).catch(() => setIsAdmin(false));
    api.get("/workspace").then(r => {
      const code = r.data?.analysis_context?.currency_code;
      if (code) setWsCcy(code.toUpperCase());
    }).catch(() => {});
    return () => {
      // stop audio on unmount
      if (audioRef.current) {
        audioRef.current.pause();
        URL.revokeObjectURL(audioRef.current.src);
      }
    };
  }, []);

  const generateBriefing = async () => {
    setBriefingLoading(true);
    try {
      const r = await api.post("/briefings/generate");
      setBriefing(r.data);
      // invalidate any cached audio for the previous briefing
      if (audioRef.current) {
        audioRef.current.pause();
        URL.revokeObjectURL(audioRef.current.src);
        audioRef.current = null;
        setAudioState("idle");
      }
      toast.success("Monday briefing ready.");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Briefing failed");
    }
    setBriefingLoading(false);
  };

  const toggleAudio = async () => {
    if (!briefing) return;
    // Pause if currently playing
    if (audioState === "playing" && audioRef.current) {
      audioRef.current.pause();
      setAudioState("idle");
      return;
    }
    // Resume if previously created (cached audio element)
    if (audioRef.current && audioRef.current.src) {
      audioRef.current.play().then(() => setAudioState("playing")).catch(() => setAudioState("idle"));
      return;
    }
    // First-time fetch: stream the MP3 from the backend
    setAudioState("loading");
    try {
      const r = await api.get(`/briefings/${briefing.id}/audio`, { responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      const audio = new Audio(url);
      audio.addEventListener("ended", () => setAudioState("idle"));
      audio.addEventListener("error", () => {
        setAudioState("idle");
        toast.error("Audio playback failed");
      });
      audioRef.current = audio;
      await audio.play();
      setAudioState("playing");
    } catch (e) {
      setAudioState("idle");
      toast.error(e?.response?.status === 503 ? "Audio synthesis unavailable" : "Couldn't play briefing audio");
    }
  };

  const openPdf = (path, filename) => {
    setPdfModal({ open: true, path, filename });
  };

  const closePdf = () => setPdfModal({ open: false, path: "", filename: "" });

  const exportPdf = () => openPdf("/reports/board", `DARA_Board_${Date.now()}.pdf`);
  const exportGuide = () => openPdf("/reports/founder-guide", "DARA_Founders_Guide.pdf");
  const exportPitchDeck = () => openPdf("/reports/pitch-deck", "DARA_OS_Investor_Pitch_Deck.pdf");

  return (
    <div className="p-4 sm:p-6 space-y-6 min-w-0" data-testid="overview-page">
      {/* header strip */}
      <div className="flex items-end justify-between flex-wrap gap-4">
        <div className="min-w-0">
          <div className="font-mono text-[10px] uppercase tracking-widest text-zinc-500">OVERVIEW · MISSION CONTROL</div>
          <h1 className="font-heading font-black text-2xl sm:text-3xl lg:text-4xl tracking-tighter uppercase mt-1 break-words">
            Good day, <span className="text-[#FFB000]">{user?.name?.split(" ")[0]}</span>.
          </h1>
          <div className="font-mono text-[11px] text-zinc-500 mt-1">
            {user?.company} · {user?.industry}
          </div>
        </div>
      </div>

      {/* EMPTY STATE — no real sources connected yet */}
      {m && m.has_real_sources === false ? (
        <Panel testId="overview-empty" className="p-12 text-center max-w-3xl mx-auto" title="// SET UP YOUR WORKSPACE">
          <Database className="w-10 h-10 text-zinc-700 mx-auto mb-4" />
          <h2 className="font-heading font-black text-3xl tracking-tight mb-3">
            Connect your first data source.
          </h2>
          <p className="text-sm text-zinc-400 mb-6 max-w-xl mx-auto leading-relaxed">
            Upload a CSV from any system, or connect Snowflake / Salesforce. DARA will profile every column, detect leaks &amp; outliers,
            and turn the columns it sees into a quantified revenue map. KPIs, the briefing, and the insight engine stay quiet until your real data lands.
          </p>
          <div className="flex items-center justify-center gap-3 flex-wrap">
            <Link to="/app/sources" data-testid="overview-add-source-cta" style={{ backgroundColor: "var(--brand-accent, #FFB000)", color: "var(--brand-accent-text, #000)" }} className="font-bold font-mono text-[11px] uppercase tracking-widest px-6 py-3 hover:opacity-90 transition-opacity inline-flex items-center gap-2">
              <Database className="w-3.5 h-3.5" /> ADD YOUR FIRST DATA SOURCE
            </Link>
            {m.sample_sources > 0 && (
              <Link to="/app/sources" className="font-mono text-[11px] uppercase tracking-widest text-zinc-500 hover:text-[#FFB000]" data-testid="overview-view-samples">
                or explore {m.sample_sources} sample source{m.sample_sources === 1 ? "" : "s"} →
              </Link>
            )}
          </div>
        </Panel>
      ) : (
      <>
      {/* Currency auto-detect banner — one-click workspace localization */}
      <CurrencyMismatchBanner />

      {/* TOOLBAR — only when there's real data to act on */}
      <div className="flex justify-end gap-2 flex-wrap">
        <button
          onClick={exportPdf}
          data-testid="export-pdf-btn"
          className="border border-zinc-800 px-4 py-2 font-mono text-[11px] uppercase tracking-widest hover:bg-zinc-900 transition-colors flex items-center gap-2"
        >
          <FileDown className="w-3.5 h-3.5" /> Board Report (PDF)
        </button>
        <button
          onClick={exportGuide}
          data-testid="export-founder-guide-btn"
          className="border border-zinc-800 px-4 py-2 font-mono text-[11px] uppercase tracking-widest hover:bg-zinc-900 transition-colors flex items-center gap-2"
          title="A plain-English playbook of everything DARA does — ideal for investor demos and non-technical stakeholders."
        >
          <BookOpen className="w-3.5 h-3.5" /> Founder&apos;s Guide (PDF)
        </button>
        {isAdmin && (
          <>
            <button
              onClick={exportPitchDeck}
              data-testid="export-pitch-deck-btn"
              className="border border-[#FFB000]/40 text-[#FFB000] px-4 py-2 font-mono text-[11px] uppercase tracking-widest hover:bg-[#FFB000]/10 transition-colors flex items-center gap-2"
              title="Master copy — super-admin only. For external sharing, use Investor Links."
            >
              <FileDown className="w-3.5 h-3.5" /> Pitch Deck (Master)
            </button>
            <Link
              to="/app/admin/pitch-links"
              data-testid="manage-pitch-links-link"
              className="border border-[#FFB000]/40 text-[#FFB000] px-4 py-2 font-mono text-[11px] uppercase tracking-widest hover:bg-[#FFB000]/10 transition-colors flex items-center gap-2"
              title="Mint per-investor magic links with watermarks + audit trail."
            >
              <FileDown className="w-3.5 h-3.5" /> Investor Links
            </Link>
          </>
        )}
        {canRecord && (
          <button
            onClick={() => setRecorderOpen(true)}
            data-testid="open-walkthrough-recorder-btn"
            className="border border-[#FFB000]/40 text-[#FFB000] px-4 py-2 font-mono text-[11px] uppercase tracking-widest hover:bg-[#FFB000]/10 transition-colors flex items-center gap-2"
            title="Record a 60-180s screencast of your DARA session and share it with your team."
          >
            <Camera className="w-3.5 h-3.5" /> Try DARA Live
          </button>
        )}
        <Link to="/app/insights" data-testid="generate-insights-cta" className="border border-zinc-800 px-4 py-2 font-mono text-[11px] uppercase tracking-widest hover:bg-zinc-900 transition-colors flex items-center gap-2">
          <Brain className="w-3.5 h-3.5" /> Insights
        </Link>
        <Link to="/app/monetization" data-testid="view-monetization-cta" style={{ backgroundColor: "var(--brand-accent, #FFB000)", color: "var(--brand-accent-text, #000)" }} className="font-bold font-mono text-[11px] uppercase tracking-widest px-4 py-2 hover:opacity-90 transition-opacity">
          Monetization Map
        </Link>
      </div>

      {/* exec briefing strip */}
      <Panel
        testId="briefing-panel"
        title="// MONDAY EXEC BRIEFING"
        meta={
          <div className="flex items-center gap-3">
            {briefing && (
              <button
                onClick={toggleAudio}
                disabled={audioState === "loading"}
                data-testid="listen-briefing-btn"
                title="Play briefing as audio"
                className="font-mono text-[10px] uppercase tracking-widest text-zinc-400 hover:text-[#FFB000] flex items-center gap-1.5 disabled:opacity-50"
              >
                {audioState === "loading" ? <Loader2 className="w-3 h-3 animate-spin" /> :
                 audioState === "playing" ? <Pause className="w-3 h-3 text-[#FFB000]" /> :
                 <Volume2 className="w-3 h-3" />}
                {audioState === "loading" ? "SYNTHESIZING…" : audioState === "playing" ? "PAUSE" : "LISTEN"}
              </button>
            )}
            <button
              onClick={generateBriefing}
              disabled={briefingLoading}
              data-testid="generate-briefing-btn"
              className="font-mono text-[10px] uppercase tracking-widest text-[#FFB000] hover:text-white flex items-center gap-1.5 disabled:opacity-50"
            >
              <Sparkles className={`w-3 h-3 ${briefingLoading ? "animate-pulse" : ""}`} />
              {briefingLoading ? "GENERATING…" : briefing ? "REGENERATE" : "GENERATE NOW"}
            </button>
          </div>
        }
      >
        <div className="p-5">
          {briefing ? (
            <>
              <div className="font-mono text-[10px] uppercase tracking-widest text-zinc-500 mb-2">
                Week of {new Date(briefing.period_end).toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" })}
              </div>
              <p className="text-sm text-zinc-200 leading-relaxed max-w-4xl" data-testid="briefing-summary">
                {briefing.summary}
              </p>
              {briefing.top_opportunities?.length > 0 && (
                <div className="mt-4 flex flex-wrap gap-3">
                  {briefing.top_opportunities.slice(0, 3).map(o => (
                    <div key={o.id} className="border border-zinc-800 px-3 py-2 flex items-center gap-3 min-w-0">
                      <span className="font-mono text-[10px] text-[#00E676] flex-shrink-0">+{fmt(o.estimated_revenue, o.estimated_revenue_currency || wsCcy)}</span>
                      <span className="font-mono text-[10px] text-zinc-400 truncate">{o.title}</span>
                    </div>
                  ))}
                </div>
              )}
            </>
          ) : (
            <div className="text-center py-3">
              <Sparkles className="w-6 h-6 text-zinc-700 mx-auto mb-2" />
              <div className="font-mono text-[11px] text-zinc-500">No briefing yet — auto-runs every Monday 08:00 UTC.</div>
              <div className="font-mono text-[10px] text-zinc-600 mt-1">Click <span className="text-[#FFB000]">Generate Now</span> to draft one immediately.</div>
            </div>
          )}
        </div>
      </Panel>

      {/* KPI grid */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <Stat testId="kpi-pipeline" label="Pipeline Value (ARR)" value={m ? fmt(m.pipeline_value, wsCcy) : "—"} delta={8.4} accent="amber" />
        <Stat testId="kpi-records" label="Records Under Management" value={m ? fmtNum(m.total_records) : "—"} delta={2.1} accent="white" />
        <Stat testId="kpi-sources" label="Active Data Sources" value={m ? `${m.active_sources}/6` : "—"} delta={0} accent="green" />
        <Stat testId="kpi-health" label="Data Health" value={m ? `${m.avg_health}%` : "—"} delta={0.3} accent="green" />
      </div>

      {/* revenue chart + feed */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <Panel testId="revenue-chart" title="// REVENUE × DARA UPLIFT · 12M" meta={m ? `Total: ${fmt(m.revenue_series?.reduce((s, x) => s + x.revenue, 0) || 0, wsCcy)}` : ""} className="lg:col-span-2">
          <div className="p-4 h-[320px]">
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={m?.revenue_series || []}>
                <defs>
                  <linearGradient id="amb" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="#FFB000" stopOpacity={0.25} />
                    <stop offset="100%" stopColor="#FFB000" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid stroke="#1a1a1a" vertical={false} />
                <XAxis dataKey="month" stroke="#71717A" tick={{ fontFamily: "JetBrains Mono", fontSize: 10 }} />
                <YAxis stroke="#71717A" tick={{ fontFamily: "JetBrains Mono", fontSize: 10 }} tickFormatter={(v) => fmt(v, wsCcy)} />
                <Tooltip
                  contentStyle={{ background: "#000", border: "1px solid #262626", fontFamily: "JetBrains Mono", fontSize: 11 }}
                  formatter={(v) => fmt(v, wsCcy)}
                />
                <Area type="monotone" dataKey="revenue" stroke="#FFB000" strokeWidth={2} fill="url(#amb)" dot={false} />
                <Line type="monotone" dataKey="ai_uplift" stroke="#00E676" strokeWidth={1.5} dot={false} />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </Panel>

        <Panel testId="insights-feed" title="// LATEST INSIGHTS" meta={`${insights.length} items`}>
          <div className="divide-y divide-zinc-900">
            {insights.length === 0 && (
              <div className="p-6 text-center">
                <Brain className="w-6 h-6 text-zinc-700 mx-auto mb-2" />
                <div className="font-mono text-[11px] text-zinc-500">No insights yet.</div>
                <Link to="/app/insights" className="font-mono text-[11px] text-[#FFB000] hover:underline">Generate now →</Link>
              </div>
            )}
            {insights.map(ins => (
              <div key={ins.id} className="p-4 hover:bg-zinc-900/30 transition-colors">
                <div className="flex items-center justify-between mb-1">
                  <span className={`font-mono text-[9px] uppercase tracking-widest ${ins.type === "ANOMALY" ? "text-[#FF3B30]" : ins.type === "PREDICTION" ? "text-[#00E676]" : "text-[#FFB000]"}`}>
                    {ins.type}
                  </span>
                  <span className="font-mono text-[9px] text-zinc-600">{ins.confidence}% conf</span>
                </div>
                <div className="text-sm text-white leading-snug mb-1">{ins.title}</div>
                <div className="font-mono text-[10px] text-[#00E676]">+{fmt(ins.estimated_value, ins.estimated_value_currency)}</div>
              </div>
            ))}
          </div>
        </Panel>
      </div>

      {/* feature strip */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {[
          { icon: Database, title: "Data Sources", desc: "Ingest and monitor every business system.", to: "/app/sources", cta: "Manage sources" },
          { icon: Brain, title: "DARA Insights", desc: "DARA-powered trends, anomalies & predictions.", to: "/app/insights", cta: "Open engine" },
          { icon: DollarSign, title: "Monetization Map", desc: "Data → DARA → Revenue, quantified in your local currency.", to: "/app/monetization", cta: "View opportunities" },
        ].map((c, i) => (
          <Link key={i} to={c.to} className="panel p-5 hover:bg-zinc-900/30 transition-colors group" data-testid={`overview-card-${i}`}>
            <c.icon className="w-5 h-5 text-[#FFB000] mb-4 stroke-[1.5]" />
            <h3 className="font-heading font-bold text-lg mb-2">{c.title}</h3>
            <p className="text-sm text-zinc-500 mb-3">{c.desc}</p>
            <span className="font-mono text-[10px] uppercase tracking-widest text-[#FFB000]">{c.cta} →</span>
          </Link>
        ))}
      </div>
      </>
      )}
      <PdfViewerModal
        open={pdfModal.open}
        path={pdfModal.path}
        filename={pdfModal.filename}
        onClose={closePdf}
      />
      <WalkthroughRecorder
        open={recorderOpen}
        onClose={() => setRecorderOpen(false)}
      />
    </div>
  );
}
